import {Form, Select, Spin} from 'antd';
import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import useRequestParams from '../../../hooks/useRequestParams';
import useSearch from '../../../hooks/useSearch';
import {setRequestParams} from '../../../state/ducks/requestParams';
import {makeIsGlobalLoading} from '../../../state/selectors/global';
import {dispatch} from '../../../state/store';
import helpers from '../../../utils/helpers';

// todo: Ostrovskyi D. - hardcoded value should be moved to the higher level
const INCREMENT_SIZE = 50;
const UNLOCK_DELAY_MS = 500;
const MAX_LOCK_MS = 2000;

const FormSelect = React.memo((props) => {
    const {
        placeholder,
        onSelect,
        onChange,
        withSearch = false,
        disabled,
        form,
        name,
        fieldLabel,
        fieldValue = 'key',
        requestOptions,
        withInitialRequest = true,
        itemPlacement,
        allowClear,
        mode,
        disabledOptionKeys = [],
    } = props;

    const [searchValue, setSearchValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isFetchDataOnLoad, setIsFetchDataOnLoad] = useState(withInitialRequest);
    const containerRef = useRef(null);
    const popupListRef = useRef(null);
    const scrollLockRef = useRef(null);
    const unlockTimerRef = useRef(null);
    const {selectRequestData, getRequestData, paramsType, options} = requestOptions || {};

    const isGlobalLoading = useSelector(makeIsGlobalLoading());
    const selectData = options?.length
        ? options
        : useSelector((state) => (selectRequestData ? selectRequestData()(state) : null));

    const {hasNextPage, items, size} = selectData || {};
    const currentValue = Form.useWatch(name, form);

    const [requestParams] = useRequestParams({
        handler: async () => {
            if (!paramsType || (!withInitialRequest && !isFetchDataOnLoad)) return;

            await getSelectData();
        },
        paramsType,
    });

    useSearch({paramsType, searchValue});

    const changeRequestParams = (data) => {
        if (!paramsType) return;

        dispatch(setRequestParams({paramsType, paramsProps: data}));
    };

    const getSelectData = async () => {
        if (disabled) return;

        helpers.runFunction(getRequestData, requestParams);
    };

    const unlockScroll = () => {
        clearTimeout(unlockTimerRef.current);
        scrollLockRef.current = null;
    };

    const lockScroll = (scrollTop) => {
        clearTimeout(unlockTimerRef.current);
        scrollLockRef.current = scrollTop;
        unlockTimerRef.current = setTimeout(unlockScroll, MAX_LOCK_MS);
    };

    const onScroll = (e) => {
        const {target} = e;

        popupListRef.current = target;

        const lockedScrollTop = scrollLockRef.current;

        if (lockedScrollTop !== null) {
            if (target.scrollTop < lockedScrollTop) {
                target.scrollTop = lockedScrollTop;
            } else {
                scrollLockRef.current = target.scrollTop;
            }

            return;
        }

        const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        const isNearBottom = distanceToBottom < 100;

        if (isNearBottom && hasNextPage && !isGlobalLoading) {
            lockScroll(target.scrollTop);
            changeRequestParams({size: size + INCREMENT_SIZE});
        }
    };

    useLayoutEffect(() => {
        const list = popupListRef.current;
        const lockedScrollTop = scrollLockRef.current;

        if (!isOpen || !list || lockedScrollTop === null) return;

        if (list.scrollTop < lockedScrollTop) list.scrollTop = lockedScrollTop;

        clearTimeout(unlockTimerRef.current);
        unlockTimerRef.current = setTimeout(unlockScroll, UNLOCK_DELAY_MS);
    }, [items?.length]);

    useEffect(() => () => clearTimeout(unlockTimerRef.current), []);

    useEffect(() => {
        if (!isOpen) return;

        const resetScroll = () => {
            const list = containerRef.current?.querySelector('.rc-virtual-list-holder');

            if (list) list.scrollTop = 0;
        };

        resetScroll();
        const timeoutId = setTimeout(resetScroll, 0);

        return () => clearTimeout(timeoutId);
    }, [isOpen]);

    const onClick = async () => {
        const isDataExist = selectData?.length ? selectData : selectData?.items;

        if (isDataExist) return;

        await getSelectData();
    };

    const onClear = () => form.setFieldValue(name);

    const notFoundContent = !items?.length && (
        <div className='flex justify-center items-center'>
            {isGlobalLoading ? <Spin size='small' /> : <span>Пусто&#128528;</span>}{' '}
        </div>
    );

    const onDropdownVisibleChange = async (open) => {
        if (isGlobalLoading) return;

        unlockScroll();
        popupListRef.current = null;

        setIsOpen(open);
        setIsFetchDataOnLoad(true);
    };

    const handleSelect = (value, option) => {
        helpers.runFunction(onSelect, [value, name, option]);
    };

    const handleChange = (value, option) => {
        helpers.runFunction(onChange, [value, name, option]);
    };

    const processedOptions = useMemo(() => {
        const rawOptions = selectData?.length ? selectData : selectData?.items;

        if (!rawOptions || disabledOptionKeys.length === 0) return rawOptions;

        return rawOptions.map((option) => ({
            ...option,
            disabled: disabledOptionKeys.includes(option[fieldValue]),
        }));
    }, [selectData, disabledOptionKeys, fieldValue]);

    return (
        <div ref={containerRef}>
            <Select
                onClick={onClick}
                onClear={onClear}
                onSelect={handleSelect}
                onChange={handleChange}
                onSearch={withSearch && setSearchValue}
                onDropdownVisibleChange={onDropdownVisibleChange}
                getPopupContainer={(trigger) => trigger.parentElement}
                open={isOpen}
                allowClear={allowClear}
                options={processedOptions}
                disabled={disabled}
                placeholder={placeholder}
                onPopupScroll={onScroll}
                searchValue={searchValue}
                defaultValue={currentValue}
                value={currentValue}
                virtual={false}
                placement={items && itemPlacement}
                showSearch={withSearch}
                filterOption={(inputValue, option) => option}
                notFoundContent={notFoundContent}
                prefixCls='ep-form-item-select'
                size='large'
                mode={mode}
                fieldNames={{
                    label: fieldLabel,
                    value: fieldValue,
                }}
            />
        </div>
    );
});

FormSelect.displayName = 'FormSelect';

export default FormSelect;
